import {
  Box,
  HStack,
  IconButton,
  Input,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import * as React from "react";
import { FaLinkedinIn, FaMeta, FaShopify } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { LuList, LuZap } from "react-icons/lu";
import { SiHubspot } from "react-icons/si";
import { Link as RouterLink, useParams } from "react-router-dom";

import {
  connectIntegration,
  disconnectIntegration,
  getFacebookPageFeed,
  getFacebookPages,
} from "../../api/integration";
// import { uploadProductMargins } from "../../api/workspace";
import { Button } from "../../components/button";
import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "../../components/dialog";
import { EmptyState } from "../../components/empty-state";
import { Field } from "../../components/field";
import PageBody from "../../components/page-body";
import {
  ProgressCircleRing,
  ProgressCircleRoot,
} from "../../components/progress-circle";
import { useWorkspaceURL } from "../../hooks";
import {
  useAppDispatch,
  useAppSelector,
  useLoading,
  useToaster,
} from "../../hooks";
import { formatCardinality } from "../../lib/cardinality";
import {
  EXPIRY_WARNING_DAYS,
  daysUntilExpiry,
} from "../../lib/integration-expiry";
import { loadIntegrations } from "../../store/workspaceSlice";
import { FacebookPage, FacebookPageFeedItem } from "../../types/facebook";
import {
  IntegrationContextField,
  IntegrationContextFieldType,
  IntegrationSchema,
  IntegrationStatus,
  IntegrationStyle,
} from "../../types/integration";

export default function IntegrationsPage() {
  const dispatch = useAppDispatch();
  const { integrations, slug } = useAppSelector((s) => s.workspace);
  const withToast = useToaster();
  const connect = React.useCallback(
    async (integration: IntegrationSchema, context: Record<string, any>) => {
      await withToast(async () => {
        switch (integration.style) {
          case IntegrationStyle.custom:
            {
              await connectIntegration(slug, integration.id, context);
              document.location.reload();
            }
            break;
          case IntegrationStyle.oauth:
            {
              const { "oauth-url": oauthURL } = await connectIntegration(
                slug,
                integration.id,
                context,
              );
              if (oauthURL === undefined) {
                document.location.reload();
              } else {
                document.location.href = oauthURL;
              }
            }
            break;
        }
      });
    },
    [slug, withToast],
  );
  const disconnect = React.useCallback(
    async (integration: IntegrationSchema) => {
      await disconnectIntegration(slug, integration.id);
      await dispatch(loadIntegrations()).unwrap();
    },
    [dispatch, slug],
  );
  React.useEffect(() => {
    dispatch(loadIntegrations());
  }, [dispatch]);

  // const [isUploadingMargins, wrapIsUploadingMargins] = useLoading(false);
  // const uploadMargins = React.useCallback(
  //   async ({ files }: { files: File[] }) => {
  //     await wrapIsUploadingMargins(async () => {
  //       await withToast(async () => {
  //         if (files.length !== 1) return;
  //         await uploadProductMargins(slug, files[0]);
  //       });
  //     });
  //   },
  //   [slug, withToast, wrapIsUploadingMargins],
  // );

  return (
    <PageBody>
      <Stack gap="5">
        <Box
          background="white"
          borderRadius="l3"
          boxShadow="card"
          maxWidth={"760px" as any}
          overflow="hidden"
          width="full"
        >
          {integrations.map((schema, index) => (
            <Box
              key={schema.id}
              borderColor="border.soft"
              borderTopWidth={index > 0 ? "1" : "0"}
            >
              <IntegrationCard
                {...schema}
                onConnect={async (context) => await connect(schema, context)}
                onDisconnect={async () => await disconnect(schema)}
              />
            </Box>
          ))}
        </Box>
        {/* <Stack>
          <SectionHeader
            title="Products"
            description="Manage your profit margins."
          />
          <HStack maxW="md">
            <Button colorPalette="gray" asChild>
              <Link href={`/api/v1/workspaces/${slug}/products.csv`}>
                <LuDownload /> Download Products
              </Link>
            </Button>

            <FileUpload.Root accept={["text/csv"]} onFileAccept={uploadMargins}>
              <FileUpload.HiddenInput />
              <FileUpload.Trigger asChild>
                <Button colorPalette="gray" loading={isUploadingMargins}>
                  <LuUpload /> Upload Product Margins
                </Button>
              </FileUpload.Trigger>
            </FileUpload.Root>
          </HStack>
          <HStack maxW="xl">
            <Text color="fg.muted">
              <strong>Instructions:</strong> First, download your list of
              products. Then, update the cost columns of each product. Finally,
              upload the updated file.
            </Text>
          </HStack>
        </Stack> */}
      </Stack>
    </PageBody>
  );
}

interface IntegrationCardProps extends IntegrationSchema {
  onDisconnect: () => Promise<void>;
  onConnect: (context: Record<string, any>) => Promise<void>;
}

function IntegrationCard({
  context,
  "expires-at": expiresAt,
  id,
  label,
  onConnect,
  onDisconnect,
  status,
}: IntegrationCardProps) {
  const { workspace } = useParams();
  const workspaceURL = useWorkspaceURL();
  const [isLoading, wrapLoading] = useLoading();
  const connect = React.useCallback(
    async (context?: Record<string, any>) => {
      await wrapLoading(async () => {
        await onConnect(context ?? {});
      });
    },
    [onConnect, wrapLoading],
  );
  const disconnect = React.useCallback(async () => {
    await wrapLoading(async () => {
      await onDisconnect();
    });
  }, [onDisconnect, wrapLoading]);

  let action: React.ReactNode;
  switch (status) {
    case IntegrationStatus.connected:
      {
        action = (
          <Button
            w="full"
            onClick={disconnect}
            loading={isLoading}
            variant="danger"
          >
            Disconnect
          </Button>
        );

        if (id === "facebook" && workspace === "meta-app-review") {
          action = (
            <HStack>
              <FacebookPagesDialog />
              {action}
            </HStack>
          );
        }

        if (id === "shopify") {
          action = (
            <VStack w="full">
              <Button asChild variant="surface" size="sm" w="full">
                <RouterLink to={workspaceURL("customer-value")}>
                  Customer Value
                </RouterLink>
              </Button>
              {action}
            </VStack>
          );
        }

        // Reconnecting is just connecting again: it reuses the row and
        // mints a fresh token. Prompt before the old one expires, since
        // once it does the integration breaks with no way to recover it
        // other than this same flow.
        const expiresInDays = daysUntilExpiry(expiresAt);
        if (expiresInDays !== null && expiresInDays <= EXPIRY_WARNING_DAYS) {
          action = (
            <VStack w="full" gap="2">
              <Text color="fg.warning" fontSize="sm" textAlign="center">
                {expiresInDays <= 0
                  ? "Connection expired — reconnect to keep it active."
                  : `Expires in ${formatCardinality(expiresInDays, "day")} — reconnect to keep it active.`}
              </Text>
              {/* Reconnecting re-runs the connect flow, so it needs the
                  same context the first connection did. LinkedIn is the
                  only integration reporting an expiry today and takes no
                  context, but don't make the next one a broken flow. */}
              {context.length > 0 ? (
                <ConnectContextDialog
                  connect={connect}
                  id={id}
                  label={label}
                  isLoading={isLoading}
                  schema={context}
                  trigger={
                    <Button w="full" loading={isLoading} variant="surface">
                      Reconnect
                    </Button>
                  }
                />
              ) : (
                <Button
                  w="full"
                  onClick={() => connect()}
                  loading={isLoading}
                  variant="surface"
                >
                  Reconnect
                </Button>
              )}
              {action}
            </VStack>
          );
        }
      }
      break;
    default: {
      action = (
        <Button
          w="full"
          onClick={() => connect()}
          loading={isLoading}
          variant="solid"
        >
          Connect
        </Button>
      );

      if (context.length > 0) {
        action = (
          <ConnectContextDialog
            connect={connect}
            id={id}
            label={label}
            isLoading={isLoading}
            schema={context}
          />
        );
      }
    }
  }

  let icon: React.ReactNode;
  switch (id) {
    case "facebook":
    case "facebook-capi":
      icon = (
        <BrandChip background="brand.meta" color="white">
          <FaMeta size="18" />
        </BrandChip>
      );
      break;
    case "google-ads":
      icon = (
        <BrandChip background="white" boxShadow="insetRing">
          <FcGoogle size="18" />
        </BrandChip>
      );
      break;
    case "gohighlevel":
      icon = (
        <BrandChip background="bg.muted" color="fg">
          <LuZap size="18" />
        </BrandChip>
      );
      break;
    case "hubspot":
      icon = (
        <BrandChip background="brand.hubspot" color="white">
          <SiHubspot size="18" />
        </BrandChip>
      );
      break;
    case "linkedin":
      icon = (
        <BrandChip background="brand.linkedin" color="white">
          <FaLinkedinIn size="18" />
        </BrandChip>
      );
      break;
    case "shopify":
      icon = (
        <BrandChip background="brand.shopify" color="white">
          <FaShopify size="18" />
        </BrandChip>
      );
      break;
  }

  return (
    <HStack align="center" gap="4" paddingX="6" paddingY="4">
      <HStack flex="1" gap="3" minWidth="0">
        {icon}
        <Text color="fg" fontSize="sm" fontWeight="medium" truncate>
          {label}
        </Text>
      </HStack>
      <Box flexShrink="0" width={"132px" as any}>
        {action}
      </Box>
    </HStack>
  );
}

function BrandChip({
  children,
  ...props
}: React.PropsWithChildren<Record<string, unknown>>) {
  return (
    <HStack
      borderRadius="l2"
      flexShrink="0"
      height="8"
      justify="center"
      width="8"
      {...(props as any)}
    >
      {children}
    </HStack>
  );
}

interface ConnectContextDialogProps {
  connect: (context: Record<string, any>) => Promise<void>;
  id: string;
  label: string;
  isLoading: boolean;
  schema: IntegrationContextField[];
  trigger?: React.ReactNode;
}

function ConnectContextDialog(props: ConnectContextDialogProps) {
  const [context, setContext] = React.useState<Record<string, any>>({});
  return (
    <DialogRoot>
      <DialogTrigger asChild>
        {props.trigger ?? <Button w="full">Connect</Button>}
      </DialogTrigger>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const filtered = Object.fromEntries(
              Object.entries(context).filter(([, v]) => v !== ""),
            );
            props.connect(filtered);
          }}
        >
          <DialogHeader>Connect {props.label}</DialogHeader>
          <DialogBody>
            <Stack gap="3">
              {props.schema.map((field) => {
                let effectiveRequired = field.required;
                if (field.id === "client-id" && context["client-secret"]) {
                  effectiveRequired = true;
                } else if (
                  field.id === "client-secret" &&
                  context["client-id"]
                ) {
                  effectiveRequired = true;
                }
                return (
                  <ConnectContextField
                    {...field}
                    required={effectiveRequired}
                    key={field.id}
                    onChange={(value) =>
                      setContext({ ...context, [field.id]: value })
                    }
                  />
                );
              })}
            </Stack>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline">Cancel</Button>
            </DialogActionTrigger>
            <Button type="submit" loading={props.isLoading}>
              Connect
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}

interface ConnectContextFieldProps extends IntegrationContextField {
  onChange: (value: string) => void;
}

function ConnectContextField({
  label,
  onChange,
  required,
  type,
}: ConnectContextFieldProps) {
  const isRequired = required !== false;
  switch (type) {
    case IntegrationContextFieldType.AccessToken:
    case IntegrationContextFieldType.FacebookCAPIDataset:
      return (
        <Field label={label}>
          <Input
            required={isRequired}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
      );

    case IntegrationContextFieldType.ShopifyAccessToken:
      return (
        <Field label={label}>
          <Input
            required={isRequired}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
      );

    case IntegrationContextFieldType.ShopifyShopURL:
      return (
        <Field label={label}>
          <HStack>
            {"https://"}
            <Input
              required={isRequired}
              onChange={(e) => onChange(e.target.value)}
            />
            {".myshopify.com"}
          </HStack>
        </Field>
      );

    case IntegrationContextFieldType.Text:
      return (
        <Field
          label={label}
          optionalText={
            isRequired ? undefined : (
              <Text
                as="span"
                color="fg.muted"
                fontWeight="normal"
                fontSize="sm"
              >
                {" "}
                (Optional)
              </Text>
            )
          }
        >
          <Input
            required={isRequired}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
      );
  }
}

function FacebookPagesDialog() {
  const { workspace } = useParams();
  const [loading, wrapLoading] = useLoading();
  const [pages, setPages] = React.useState<FacebookPage[]>([]);
  React.useEffect(() => {
    wrapLoading(async () => {
      setPages(await getFacebookPages(workspace));
    });
  }, [workspace, wrapLoading]);

  return (
    <DialogRoot>
      <DialogTrigger asChild>
        <IconButton variant="subtle">
          <LuList />
        </IconButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>Facebook Pages</DialogHeader>
        <DialogBody>
          {loading ? (
            <ProgressCircleRoot>
              <ProgressCircleRing />
            </ProgressCircleRoot>
          ) : pages.length === 0 ? (
            <EmptyState
              icon={<LuList />}
              title="No Pages"
              description="You don't have any Facebook pages."
            />
          ) : (
            <VStack align="left">
              {pages.map((p) => (
                <HStack
                  key={p.id}
                  borderRadius="sm"
                  p="2"
                  _hover={{ background: "bg.muted" }}
                >
                  <Text>{p.name}</Text>
                  <Spacer />
                  <FacebookPageFeedDialog page={p} />
                </HStack>
              ))}
            </VStack>
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}

interface FacebookPageFeedDialogProps {
  page: FacebookPage;
}

function FacebookPageFeedDialog({ page }: FacebookPageFeedDialogProps) {
  const [loading, wrapLoading] = useLoading();
  const [items, setItems] = React.useState<FacebookPageFeedItem[]>([]);
  React.useEffect(() => {
    wrapLoading(async () => {
      setItems(await getFacebookPageFeed(page));
    });
  }, [page, wrapLoading]);

  return (
    <DialogRoot>
      <DialogTrigger asChild>
        <IconButton variant="subtle">
          <LuList />
        </IconButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>{page.name}</DialogHeader>
        <DialogBody>
          {loading ? (
            <ProgressCircleRoot>
              <ProgressCircleRing />
            </ProgressCircleRoot>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<LuList />}
              title="No Feed Items"
              description="This page has no feed items."
            />
          ) : (
            <VStack align="left">
              {items.map((i, idx) => (
                <HStack key={idx} p="2">
                  <Text>{i.message}</Text>
                </HStack>
              ))}
            </VStack>
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
